import React, { useRef, useState } from "react";
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import CloseIcon from '@mui/icons-material/Close';

// GenericPopupTable.tsx (MUI Popover version)
// - Uses MUI Popover for positioning
// - Exports default GenericPopupTable which accepts anchorEl
// - Exports ProductBatchPopover wrapper for the "first batch id +N" behaviour

type Column = {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  render?: (row: any) => React.ReactNode;
};

type GenericProps = {
  columns: Column[];
  rows: any[];
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  title?: string;
  footer?: React.ReactNode;
  maxHeight?: string;
};

function ExpiryBadge({ date }: { date?: string | Date | null }) {
  if (!date) return <Typography variant="body2" color="text.secondary">N/A</Typography>;
  const d = typeof date === 'string' ? new Date(date) : date instanceof Date ? date : null;
  if (!d) return <Typography variant="body2" color="text.secondary">{String(date)}</Typography>;

  const sx = {
    px: 1.5,
    py: 0.4,
    borderRadius: 3,
    fontWeight: 500,
    fontSize: '0.75rem',
    display: 'inline-block',
    background: 'linear-gradient(to right, #FFE8E8, transparent)',
    color: '#B54747',
  } as const;

  return <Box component="span" sx={sx}>{d.toLocaleDateString()}</Box>;
}

// ... (imports unchanged)

export default function GenericPopupTable({
  columns,
  rows,
  open,
  anchorEl,
  onClose,
  title = 'Details',
  footer,
  maxHeight = '55vh'
}: GenericProps) {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: {
          width: { xs: 200, sm: 250, md: 300 },
          maxHeight: maxHeight,      // allow Popover to grow up to the provided maxHeight
          overflow: 'visible',      // let the inner element control scrolling
          display: 'flex',
          flexDirection: 'column',
          p:1
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0, py: 0, borderBottom: '1px solid', borderColor: 'divider', width: 'min-content' }}>
        <Box>
          <Typography variant="subtitle1" component="div" fontSize="0.7rem" color="#A1A1A1" whiteSpace={"nowrap"}>
            {title} <Typography component="span" variant="caption" >({rows.length})</Typography>
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 2, py: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`, gap: 2, fontSize: '0.85rem', color: 'text.secondary', fontWeight: 600 }}>
          {columns.map((c) => (
            <Box key={c.key} sx={{ textAlign: c.align ?? 'left', fontSize: "0.72rem", fontWeight: 400, color: "#A1A1A1" }}>{c.label}</Box>
          ))}
        </Box>
      </Box>

      {/* The scrolling region: constrain height and enable overflow */}
      <Box sx={{ overflow: "auto", px: 0, flex: '1 1 auto', maxHeight: `150px`, scrollbarWidth: "none" }}>
        <List disablePadding sx={{display: "flex", flexDirection: "column", gap: "5px"}}>
          {rows.map((row, idx) => (
            <ListItem key={idx} sx={{ py: 0.5, px: 1, display: 'flex', alignItems: "center", backgroundColor: "#FCFCFC", borderBottom: (idx === rows.length - 1) ? "" : "1px dashed #E8E8E8", borderRadius: "8px" }}>
              {columns.map((col) => (
                <ListItemText
                  key={col.key}
                  primary={
                    col.render
                      ? col.render(row)
                      : (col.key.toLowerCase().includes('expiry') || col.key.toLowerCase().includes('date')
                          ? <ExpiryBadge date={row[col.key]} />
                          : String(row[col.key] ?? ''))
                  }
                  sx={{ textAlign: col.align ?? 'left', '& .MuiListItemText-primary': { fontSize: '0.75rem', fontWeight: 500 }, color:"#747474" }}
                />
              ))}
            </ListItem>
          ))}

          {rows.length === 0 && (
            <ListItem sx={{ py: 2, px: 2 }}>
              <ListItemText primary={<Typography variant="body2" color="text.secondary">No records found</Typography>} />
            </ListItem>
          )}
        </List>
      </Box>

      {footer && <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2 }}>{footer}</Box>}
    </Popover>
  );
}
